import { ClientMethod } from 'openapi-fetch'

interface Input<T> {
  method: T
  returnAllErrors?: boolean
}

// Distinguishes "fetch itself never got a response" (network drop, CORS, DNS) from a parsed
// non-2xx response, which callers get back as-is via `error` instead of this class - a definitive
// answer from the server, not an ambiguous one worth retrying.
export class NetworkFetchError extends Error {
  constructor(cause: unknown) {
    super('Fetch failed', { cause })
    this.name = 'NetworkFetchError'
  }
}

/**
 * Higher order function that is used by the OpenAPI client to capture the error instead of throwing
 *
 * NOTE: this used to capture the error in sentry, but with the shift to RUM it is better to capture the errors with direct fetch hook
 * since this utility is shared across fe/be it was not appropriate to add RUM directly to it.
 *
 * TODO(klaus): consider removing this completely, it is kept for now since it does some modifications to the error which I don't know if they are safe to remove.
 * */

export function withErrorCapture<T extends ClientMethod<any, any, any>>({ method }: Input<T>): T {
  return (async (...args: [any, ...any[]]) => {
    let result

    try {
      result = await method(...args)
    } catch (err) {
      // Catch errors which openapi-fetch throws rather than returns in response
      // e.g. CORS errors
      return { error: new NetworkFetchError(err) }
    }

    const { response, error } = result
    if (result.response.ok) {
      return result
    }

    if (!error) {
      return result
    }

    // Drop `data` but keep `response`: callers branch on the status (e.g. a gateway 504) even when
    // the error body was parsed.
    return { error, response }
  }) as T
}
